/**
 * DROPIT Supreme — landing do Supreme 001 (sections/dropit-supreme.liquid).
 *
 * - "Supreme 001" da abertura ocupa a largura toda (teto opcional: --sp-fit-max no CSS).
 * - Galeria: as miniaturas trocam a foto principal.
 * - "Adicionar ao carrinho" (1 unidade) via Shopify.actions.updateCart, que abre o carrinho do tema;
 *   sem essa API, cai no /cart/add.js. Se o Supreme já está no carrinho, só abre o carrinho.
 * - Barra fixa aparece quando o botão de compra sai da tela e some no fecho azul.
 * - data-demo (só na prévia local): liga o "revelar" que na loja é do tema.
 */
(() => {
  const root = document.querySelector('[data-sp]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = '1';

  const demo = root.hasAttribute('data-demo');

  /* ---------- Título no tamanho da tela ---------- */
  const title = root.querySelector('[data-sp-fit]');
  const fit = () => {
    if (!title) return;
    const box = title.parentElement;
    const cs = getComputedStyle(box);
    const room = box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    title.style.fontSize = '100px';
    const range = document.createRange();
    range.selectNodeContents(title);
    const w = range.getBoundingClientRect().width;
    const max = parseFloat(getComputedStyle(title).getPropertyValue('--sp-fit-max')) || Infinity;
    if (w > 0) title.style.fontSize = `${Math.min(max, Math.floor(((100 * room) / w) * 0.99))}px`;
  };
  fit();
  document.fonts?.ready.then(fit);
  // Refaz quando a largura da abertura muda (giro do celular, janela, barra lateral)
  let lastWidth = 0;
  if (title && 'ResizeObserver' in window) {
    new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      if (w === lastWidth) return;
      lastWidth = w;
      fit();
    }).observe(title.parentElement);
  } else {
    window.addEventListener('resize', fit);
  }

  /* ---------- Galeria ---------- */
  const main = root.querySelector('[data-sp-main]');
  const thumbs = [...root.querySelectorAll('[data-sp-thumb]')];
  for (const thumb of thumbs) {
    thumb.addEventListener('click', () => {
      if (thumb.getAttribute('aria-pressed') === 'true') return;
      for (const t of thumbs) t.setAttribute('aria-pressed', String(t === thumb));
      main.classList.add('is-swapping');
      setTimeout(() => {
        main.src = thumb.dataset.spThumb;
        main.alt = thumb.dataset.alt || '';
        main.classList.remove('is-swapping');
      }, 180);
    });
  }

  /* ---------- Fotos de deslizar (celular): barrinha da foto atual ---------- */
  const swipe = root.querySelector('[data-sp-swipe-track]');
  const dots = [...root.querySelectorAll('.sp-swipe__dots span')];
  if (swipe && dots.length) {
    let raf = 0;
    swipe.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(swipe.scrollLeft / Math.max(1, swipe.clientWidth));
        dots.forEach((d, n) => d.classList.toggle('is-on', n === i));
      });
    }, { passive: true });
  }

  /* ---------- Carrinho: uma unidade por pedido ---------- */
  const variant = root.dataset.variant;
  const available = root.dataset.state === 'available' && !!variant;
  const addButtons = [...root.querySelectorAll('[data-sp-add]')];
  const ctaLabels = [...root.querySelectorAll('[data-sp-cta]')];
  const msg = root.querySelector('[data-sp-msg]');
  const label = ctaLabels[0]?.textContent || '';
  const setLabel = (text) => ctaLabels.forEach((el) => (el.textContent = text));
  addButtons.forEach((b) => (b.disabled = !available));

  const openCart = () => {
    const actions = window.Shopify?.actions;
    if (actions?.openCart) actions.openCart();
    else window.location.href = '/cart';
  };

  async function alreadyInCart() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      return cart.items?.some((item) => String(item.variant_id) === String(variant));
    } catch {
      return false;
    }
  }

  async function addToCart() {
    const actions = window.Shopify?.actions;
    if (actions?.updateCart) {
      const result = await actions.updateCart({ lines: [{ merchandiseId: variant, quantity: 1 }] }, { event: { context: 'cart' } });
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
      actions.openCart?.();
      return;
    }
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variant, quantity: 1 }] }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || 'cart');
    }
    window.location.href = '/cart';
  }

  for (const b of addButtons) {
    b.addEventListener('click', async () => {
      if (!available || root.dataset.busy) return;
      root.dataset.busy = '1';
      addButtons.forEach((x) => (x.disabled = true));
      setLabel('Adicionando…');
      if (msg) msg.textContent = '';
      try {
        if (demo) {
          await new Promise((r) => setTimeout(r, 600));
          if (msg) msg.textContent = 'Prévia: 1 × Supreme 001 iria para o carrinho.';
        } else if (await alreadyInCart()) {
          if (msg) msg.textContent = 'O Supreme 001 já está no seu carrinho. Uma unidade por pessoa.';
          openCart();
        } else {
          await addToCart();
        }
        setLabel('Adicionado ✓');
        setTimeout(() => setLabel(label), 2200);
      } catch (error) {
        setLabel(label);
        if (msg) msg.textContent = error?.message && error.message !== 'cart'
          ? error.message
          : 'Não deu para adicionar agora. Tente de novo em instantes.';
      } finally {
        delete root.dataset.busy;
        addButtons.forEach((x) => (x.disabled = !available));
      }
    });
  }

  /* ---------- Barra fixa ---------- */
  const bar = root.querySelector('[data-sp-bar]');
  const buyRow = root.querySelector('.sp-buybox .sp-btn');
  const end = root.querySelector('.sp-end');
  const open = root.querySelector('.sp-open');
  if (bar && buyRow && 'IntersectionObserver' in window) {
    const seen = new Map();
    const update = () => {
      const buyAbove = seen.get(buyRow) === 'above';
      const on = buyAbove && seen.get(end) !== 'in' && seen.get(open) !== 'in';
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', String(!on));
      bar.querySelectorAll('button').forEach((b) => (b.tabIndex = on ? 0 : -1));
    };
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        seen.set(e.target, e.isIntersecting ? 'in' : e.boundingClientRect.top < 0 ? 'above' : 'below');
      }
      update();
    });
    [buyRow, end, open].filter(Boolean).forEach((el) => io.observe(el));
  }

  /* ---------- Revelar (só na prévia; na loja é o dropit-reveal.js do tema) ---------- */
  if (demo && 'IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const targets = [...root.querySelectorAll('[data-reveal], [data-reveal-group]')];
    const show = (el) => el.setAttribute('data-revealed', '');
    targets.filter((el) => el.getBoundingClientRect().top < innerHeight).forEach(show);
    document.documentElement.classList.add('sp-reveal-ready');
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { show(e.target); io.unobserve(e.target); }
    }, { rootMargin: '0px 0px -10% 0px' });
    targets.forEach((el) => io.observe(el));
  }
})();
